import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

// Approval cover (department walls, session 3): hand a department's approvals
// to someone who already approves, for a set time. The rules are enforced in
// accounts/views_delegation.py; this only offers the choices that can pass.
const WIDE = ["Owner", "Admin"];
const HANDS_OVER = ["Owner", "Admin", "Department Leader", "Manager"];
const RECEIVES = ["Admin", "Department Leader", "Manager"];
const STATE_LABEL = { active: "Active", upcoming: "Starts later", finished: "Finished", ended: "Ended early" };

const field =
  "bg-surface border border-line-control rounded-lg p-2 text-ink text-sm sm:text-base focus:border-brand [color-scheme:light]";

// fetchWithAuth hands back either a Response or the parsed body.
const body = async (r) => (r && typeof r.json === "function" ? r.json() : r);

const MAX_DAYS = 90; // DELEGATION_MAX_DAYS in accounts/department_scope.py

function today(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

// The last day a cover starting on `start` can run (90 days, start day included).
function lastDay(start) {
  const d = new Date(`${start}T00:00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  d.setDate(d.getDate() + MAX_DAYS - 1);
  return d.toISOString().slice(0, 10);
}

export default function ApprovalCover({ meId, members, departments }) {
  const me = members.find((m) => m.user_id === meId);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ department: "", to_user: "", starts_on: today(), ends_on: today(7) });
  const [busy, setBusy] = useState(false);

  const canHandOver = !!me && HANDS_OVER.includes(me.role);
  const wide = !!me && WIDE.includes(me.role);
  const ownDepts = useMemo(
    () => (wide ? departments : departments.filter((d) => d.id === me?.department_id)),
    [wide, departments, me]
  );
  const takers = members.filter((m) => RECEIVES.includes(m.role) && m.user_id !== meId);

  const load = async () => {
    const r = await body(await fetchWithAuth("/api/accounts/approval-delegations/"));
    setRows(Array.isArray(r?.results) ? r.results : []);
  };
  useEffect(() => {
    if (me) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meId, !!me]);

  if (!me || (!canHandOver && rows.length === 0)) return null;

  const dept = form.department || (ownDepts.length === 1 ? String(ownDepts[0].id) : "");

  const submit = async () => {
    setBusy(true);
    try {
      const res = await body(await fetchWithAuth("/api/accounts/approval-delegations/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, department: dept }),
      }));
      if (!res?.id) throw new Error(res?.error || "Could not hand over approvals.");
      toast.success(`${res.to_name} covers ${res.department_name} approvals until ${res.ends_on}. It ends by itself then.`);
      setForm((f) => ({ ...f, to_user: "" }));
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const end = async (row) => {
    const res = await body(await fetchWithAuth(`/api/accounts/approval-delegations/${row.id}/end/`, { method: "POST" }));
    if (res?.state === "ended") {
      toast.success("Ended.");
      load();
    } else {
      toast.error(res?.error || "Could not end it.");
    }
  };

  return (
    <div className="mb-6 bg-surface border border-line rounded-2xl p-3 sm:p-4">
      <div className="text-base sm:text-lg font-semibold text-ink">Approval cover</div>
      <p className="text-xs sm:text-sm text-tertiary mt-1 mb-3">
        Going on leave or traveling? Hand your department's timecard, travel and milestone approvals to someone who
        already approves, for up to 90 days. They approve only what you could, never their own work, and every
        approval they make is in the action log under both names. You keep your own approvals, and it ends by itself.
      </p>

      {canHandOver && ownDepts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-3">
          {wide ? (
            <select aria-label="Department to hand over" value={dept} className={field}
              onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}>
              <option value="">Department</option>
              {ownDepts.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          ) : (
            <div className="p-2 text-sm text-ink self-center">{ownDepts[0]?.name}</div>
          )}
          <select aria-label="Who covers" value={form.to_user} className={field}
            onChange={(e) => setForm((f) => ({ ...f, to_user: e.target.value }))}>
            <option value="">Who covers</option>
            {takers.map((m) => (
              <option key={m.user_id} value={m.user_id}>{m.name || m.email} ({m.role})</option>
            ))}
          </select>
          <input type="date" aria-label="Cover starts" value={form.starts_on} className={field}
            onChange={(e) => setForm((f) => ({ ...f, starts_on: e.target.value }))} />
          <input type="date" aria-label="Cover ends" value={form.ends_on} className={field}
            min={form.starts_on} max={lastDay(form.starts_on)}
            onChange={(e) => setForm((f) => ({ ...f, ends_on: e.target.value }))} />
          <button type="button" onClick={submit} disabled={busy || !dept || !form.to_user}
            className="bg-brand hover:bg-brand-hover text-white px-3 py-2 rounded-pill text-sm disabled:opacity-60 disabled:cursor-not-allowed">
            {busy ? "Saving..." : "Hand over"}
          </button>
        </div>
      )}
      {canHandOver && ownDepts.length > 0 && (
        <p className="text-xs sm:text-sm text-ink -mt-1 mb-3">
          A cover lasts up to 90 days. It ends by itself on the end date you pick, and you can end it sooner here.
        </p>
      )}
      {canHandOver && ownDepts.length === 0 && (
        <p className="text-xs sm:text-sm text-tertiary mb-3">You need a department before you can hand its approvals over.</p>
      )}

      {rows.length > 0 && (
        <ul className="divide-y divide-line text-xs sm:text-sm">
          {rows.map((r) => (
            <li key={r.id} className="py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="text-ink">
                {r.department_name}: {r.from_name} to {r.to_name}, {r.starts_on} to {r.ends_on}
                <span className="text-tertiary"> · {STATE_LABEL[r.state] || r.state}</span>
              </span>
              {(r.state === "active" || r.state === "upcoming") && (
                <button type="button" onClick={() => end(r)}
                  className="text-xs border border-line-strong bg-surface text-ink px-3 py-2 rounded-pill hover:border-brand hover:text-brand self-start sm:self-auto">
                  End now
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
